import { clearBrowserCookies, clearWebStorage } from '@/lib/storage/browserStorage';

const AMP_TEST_COOKIE_PREFIXES = ['AMP_TEST_', 'AMP_TLDTEST_'];
const AMP_SESSION_REPLAY_DB_PATTERN = /^[a-zA-Z0-9]{10}_amp_session_replay_events/;

const getApiKeyPrefix = (apiKey: string) => apiKey.substring(0, 10);

const isAmplitudeCookie = (name: string, apiKeyPrefix: string) => {
  if (AMP_TEST_COOKIE_PREFIXES.some((prefix) => name.startsWith(prefix))) {
    return true;
  }

  if (!apiKeyPrefix) {
    return name.startsWith('AMP_');
  }

  return (
    name === `AMP_${apiKeyPrefix}` ||
    name === `AMP_MKTG_${apiKeyPrefix}` ||
    name.startsWith(`AMP_${apiKeyPrefix}_`)
  );
};

export const clearAmplitudeCookies = (apiKey: string) => {
  if (!apiKey) {
    return;
  }

  const apiKeyPrefix = getApiKeyPrefix(apiKey);
  clearBrowserCookies((name) => isAmplitudeCookie(name, apiKeyPrefix));
};

const isAmplitudeAnalyticsLocalStorageKey = (key: string, apiKeyPrefix: string) => {
  if (!key.startsWith('AMP_')) {
    return false;
  }

  if (!apiKeyPrefix) {
    return true;
  }

  return (
    key.includes(apiKeyPrefix) ||
    key.startsWith(`AMP_unsent_${apiKeyPrefix}`) ||
    key.startsWith(`AMP_failed_${apiKeyPrefix}`) ||
    key.startsWith(`AMP_replay_unsent_${apiKeyPrefix}`)
  );
};

const isAmplitudeExperimentLocalStorageKey = (key: string) =>
  key.startsWith('amp-exp-') || key.startsWith('EXP_');

const isAmplitudeExperimentSessionStorageKey = (key: string) =>
  key.startsWith('amp-exp-') || key.startsWith('EXP_');

const clearAmplitudeWebStorage = (apiKeyPrefix: string) => {
  clearWebStorage(
    () => window.localStorage,
    (key) =>
      isAmplitudeAnalyticsLocalStorageKey(key, apiKeyPrefix) ||
      isAmplitudeExperimentLocalStorageKey(key)
  );
  clearWebStorage(() => window.sessionStorage, isAmplitudeExperimentSessionStorageKey);
};

const deleteIndexedDbOnce = (name: string) =>
  new Promise<'success' | 'blocked' | 'error'>((resolve) => {
    try {
      const request = indexedDB.deleteDatabase(name);
      request.onsuccess = () => resolve('success');
      request.onerror = () => resolve('error');
      request.onblocked = () => resolve('blocked');
    } catch {
      resolve('error');
    }
  });

const deleteIndexedDb = async (name: string) => {
  const maxAttempts = 10;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const result = await deleteIndexedDbOnce(name);

    if (result === 'success') {
      return;
    }

    if (result === 'error') {
      return;
    }

    if (attempt < maxAttempts - 1) {
      // Give session replay time to release IndexedDB connections after teardown.
      const retryDelayMs = Math.min(50 * 2 ** attempt, 500);
      await new Promise((resolve) => {
        setTimeout(resolve, retryDelayMs);
      });
    }
  }
};

const isAmplitudeSessionReplayDatabase = (name: string, apiKeyPrefix: string) => {
  if (!apiKeyPrefix) {
    return AMP_SESSION_REPLAY_DB_PATTERN.test(name);
  }

  return name.startsWith(`${apiKeyPrefix}_amp_session_replay_events`);
};

const clearAmplitudeIndexedDb = async (apiKeyPrefix: string) => {
  if (typeof indexedDB === 'undefined') {
    return;
  }

  const databaseNames = new Set<string>();

  if (apiKeyPrefix) {
    databaseNames.add(`${apiKeyPrefix}_amp_session_replay_events`);
    databaseNames.add(`${apiKeyPrefix}_amp_session_replay_events_interaction`);
  }

  if (typeof indexedDB.databases === 'function') {
    try {
      const databases = await indexedDB.databases();
      databases.forEach((database) => {
        if (database.name && isAmplitudeSessionReplayDatabase(database.name, apiKeyPrefix)) {
          databaseNames.add(database.name);
        }
      });
    } catch {
      // Fall back to known database names above.
    }
  }

  await Promise.all(Array.from(databaseNames).map((name) => deleteIndexedDb(name)));
};

export const clearAmplitudeSessionReplayIndexedDb = async (apiKey: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  await clearAmplitudeIndexedDb(getApiKeyPrefix(apiKey));
};

export const clearAmplitudePersistedStorage = async (apiKey: string) => {
  if (typeof window === 'undefined' || !apiKey) {
    return;
  }

  const apiKeyPrefix = getApiKeyPrefix(apiKey);
  clearAmplitudeCookies(apiKey);
  clearAmplitudeWebStorage(apiKeyPrefix);
  await clearAmplitudeIndexedDb(apiKeyPrefix);
};
